import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { notiInfoState } from 'recoils/user/userInfoState';
import { userTokenState } from 'recoils/user/userState';

import {
  getNotificationAlarm,
  postNotificationReadAlarm,
} from 'apis/notificationApi';

import usePopupButton from 'hooks/components/usePopupButton';
import useToast from 'hooks/components/useToast';

/**
 * @desc 알림 정보를 관리하는 커스텀 Hook
 * @returns {Object} notiInfo - 알림 정보, onClickMarkAllAsRead - 전체 읽음 처리 함수, onClickNavigateToNotiLink - 알림 아이템 클릭 시 링크 이동 함수
 */
const useAlarm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userToken] = useRecoilState(userTokenState); // 유저 토큰 상태 관리
  const [notiInfo, setNotiInfo] = useRecoilState(notiInfoState);
  const { visiblePopupButton, openPopupButton, closePopupButton } =
    usePopupButton();

  /**
   * @desc 유저 토큰이 변경될 때 알림 데이터를 가져오는 효과 Hook
   * @effect
   */
  useEffect(() => {
    if (userToken) {
      if (!notiInfo.isData) {
        fetchNotificationAlarm(); // 유저 토큰이 유효하면 알림 데이터를 가져옴
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, notiInfo.isData]); // userToken이 변경될 때만 실행

  const fetchNotificationAlarm = async () => {
    try {
      const { success, data } = await getNotificationAlarm();
      if (success) {
        // 모든 알림이 읽음 상태인지 미리 계산
        const isAllRead = data.notifications.every(
          (noti) => noti.read === true
        );
        return setNotiInfo((prev) => ({
          ...prev,
          isAllRead,
          list: data.notifications,
        }));
      } else {
        return setNotiInfo((prev) => ({
          ...prev,
          list: [],
          isAllRead: true,
        }));
      }
    } catch (error) {
      setNotiInfo((prev) => ({
        ...prev,
        list: [],
        isAllRead: true,
        isData: true,
      }));
      throw error;
    }
  };

  /**
   * @desc 전체 알림을 읽음 처리하는 함수
   */
  const onClickMarkAllAsRead = useCallback(async () => {
    // PATCH) /noti/read-all - API 통신으로 처리할 부분
    try {
      const { success } = await postNotificationReadAlarm();
      if (success) {
        setNotiInfo((prev) => ({
          ...prev,
          isAllRead: true,
          isopen: !prev.isopen,
        }));
        fetchNotificationAlarm();
      }
    } catch (err) {
      toast.show(
        'error',
        '알림을 읽음 처리하지 못했습니다. 다시 시도해주세요.'
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @desc 알림 아이템 클릭 시 링크 이동하는 함수
   * @param {string} link - 클릭한 알림의 링크
   */
  const onClickNavigateToNotiLink = (link) => {
    if (link) {
      closePopupButton();
      navigate(link);
    }
  };

  return {
    notiInfo,
    onClickMarkAllAsRead,
    onClickNavigateToNotiLink,
    visiblePopupButton,
    openPopupButton,
    closePopupButton,
  }; // 알림 정보, 전체 읽음 처리, 알림 링크 이동 함수 반환
};

export default useAlarm;
